import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, X, Workflow } from "lucide-react";
import api, { fmtErr, fmtDateTime } from "../../lib/api";
import { toast } from "sonner";

const FIELDS = [["origin_pincode", "Origin pincode"], ["destination_pincode", "Destination pincode"], ["zone", "Zone"], ["weight", "Weight (kg)"], ["payment_mode", "Payment mode"], ["courier_code", "Courier"], ["provider_code", "Provider"], ["customer_email", "Customer email"], ["product_sku", "Product SKU"]];
const OPS = [["eq", "="], ["ne", "≠"], ["lt", "<"], ["gt", ">"], ["contains", "contains"]];
const ACTIONS = [["prefer_courier", "Prefer courier"], ["avoid_courier", "Avoid courier"], ["prefer_provider", "Prefer provider"]];
const inputCls = "rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-shipany-blue focus:outline-none";

export default function AdminRules() {
  const [rules, setRules] = useState(null);
  const [couriers, setCouriers] = useState([]);
  const [providers, setProviders] = useState([]);
  const [logs, setLogs] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/rules").then(({ data }) => setRules(data)).catch(() => {});
    api.get("/admin/couriers").then(({ data }) => setCouriers(data)).catch(() => {});
    api.get("/admin/providers").then(({ data }) => setProviders(data)).catch(() => {});
    api.get("/admin/routing-logs").then(({ data }) => setLogs(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => setEditing({ name: "", enabled: true, priority: 1, conditions: [{ field: "zone", op: "eq", value: "" }], action: { type: "prefer_courier", value: "" } });

  const save = async () => {
    try {
      await api.post("/admin/rules", editing);
      toast.success("Routing rule saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const targetOptions = editing?.action.type === "prefer_provider"
    ? providers.map((p) => [p.code, p.name])
    : couriers.map((c) => [c.code, c.name]);

  return (
    <div data-testid="admin-rules-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Courier Rule Engine</h1>
          <p className="text-sm text-slate-500">Visual IF → THEN routing. Applied live in every rate calculation.</p>
        </div>
        <button onClick={openNew} data-testid="rule-new-btn" className="flex items-center gap-2 rounded-xl bg-shipany-blue px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0648B3] glow-blue"><Plus className="h-4 w-4" /> New Rule</button>
      </div>

      <div className="mt-6 space-y-4" data-testid="rules-list">
        {(rules || []).map((r) => (
          <div key={r.rule_id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`rule-${r.rule_id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Workflow className={`h-5 w-5 ${r.enabled ? "text-shipany-blue" : "text-slate-300"}`} />
                <p className="font-display font-bold text-shipany-navy">{r.name} <span className="mono ml-1 text-[10px] font-normal text-slate-400">P{r.priority}</span></p>
              </div>
              <div className="flex gap-2">
                <button onClick={async () => { await api.post("/admin/rules", { ...r, enabled: !r.enabled }); load(); }} data-testid={`rule-toggle-${r.rule_id}`}
                  className={`rounded-lg px-3 py-1.5 text-xs font-bold ${r.enabled ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>{r.enabled ? "ENABLED" : "DISABLED"}</button>
                <button onClick={() => setEditing(r)} data-testid={`rule-edit-${r.rule_id}`} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-shipany-blue hover:bg-shipany-blue/5">Edit</button>
                <button onClick={async () => { if (window.confirm("Delete rule?")) { await api.delete(`/admin/rules/${r.rule_id}`); load(); } }} data-testid={`rule-delete-${r.rule_id}`}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-red-500 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
              <span className="rounded-lg bg-shipany-navy px-3 py-1.5 font-bold text-white">IF</span>
              {r.conditions.map((c, i) => (
                <span key={i} className="rounded-lg bg-slate-50 px-3 py-1.5 font-medium text-slate-600">
                  {FIELDS.find(([k]) => k === c.field)?.[1] || c.field} <b className="text-shipany-blue">{OPS.find(([k]) => k === c.op)?.[1] || c.op}</b> <span className="mono">{c.value}</span>
                </span>
              ))}
              <span className="rounded-lg bg-shipany-blue px-3 py-1.5 font-bold text-white">THEN</span>
              <span className="rounded-lg bg-shipany-cyan/15 px-3 py-1.5 font-semibold text-shipany-blue">
                {ACTIONS.find(([k]) => k === r.action.type)?.[1]}: {couriers.find((c) => c.code === r.action.value)?.name || providers.find((p) => p.code === r.action.value)?.name || r.action.value}
              </span>
            </div>
          </div>
        ))}
        {rules && rules.length === 0 && <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center text-sm text-slate-400">No rules yet — create your first routing rule.</div>}
      </div>

      <h2 className="font-display mt-10 text-lg font-bold text-shipany-navy">Routing & Failover Decisions</h2>
      <div className="mt-3 space-y-2.5" data-testid="routing-logs">
        {logs.slice(0, 10).map((l) => (
          <div key={l.log_id} className="rounded-xl border border-slate-200 bg-white px-5 py-3">
            <p className="mono text-xs text-slate-400">{l.order_number} · {fmtDateTime(l.created_at)}</p>
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              {(l.decisions || []).map((d, i) => (
                <span key={i} className={`rounded-lg px-2.5 py-1 text-[11px] font-semibold ${d.result === "success" ? "bg-emerald-50 text-emerald-700" : d.result === "demo_fallback" ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-600"}`}>
                  {d.provider_code}/{d.courier} — {d.result}{d.mode ? ` (${d.mode})` : ""}
                </span>
              ))}
            </div>
          </div>
        ))}
        {!logs.length && <p className="text-sm text-slate-400">No routing decisions logged yet — they appear on every booking.</p>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="rule-modal">
          <div className="absolute inset-0 bg-shipany-navy/60 backdrop-blur-sm" onClick={() => setEditing(null)} />
          <div className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-shipany-navy">{editing.rule_id ? "Edit Rule" : "New Routing Rule"}</h2>
              <button onClick={() => setEditing(null)} className="text-slate-400"><X className="h-5 w-5" /></button>
            </div>
            <input data-testid="rule-name" className={`${inputCls} mt-4 w-full`} placeholder="Rule name *" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            <p className="mt-4 text-xs font-bold uppercase tracking-widest text-slate-400">IF (all conditions must match)</p>
            <div className="mt-2 space-y-2">
              {editing.conditions.map((c, i) => (
                <div key={i} className="flex items-center gap-2">
                  <select data-testid={`rule-cond-field-${i}`} className={inputCls} value={c.field} onChange={(e) => { const conditions = [...editing.conditions]; conditions[i] = { ...c, field: e.target.value }; setEditing({ ...editing, conditions }); }}>
                    {FIELDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                  <select data-testid={`rule-cond-op-${i}`} className={`${inputCls} w-24`} value={c.op} onChange={(e) => { const conditions = [...editing.conditions]; conditions[i] = { ...c, op: e.target.value }; setEditing({ ...editing, conditions }); }}>
                    {OPS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                  <input data-testid={`rule-cond-value-${i}`} className={`${inputCls} mono flex-1`} placeholder="value" value={c.value} onChange={(e) => { const conditions = [...editing.conditions]; conditions[i] = { ...c, value: e.target.value }; setEditing({ ...editing, conditions }); }} />
                  <button onClick={() => setEditing({ ...editing, conditions: editing.conditions.filter((_, j) => j !== i) })} className="text-red-400"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              <button onClick={() => setEditing({ ...editing, conditions: [...editing.conditions, { field: "zone", op: "eq", value: "" }] })} data-testid="rule-cond-add-btn"
                className="rounded-lg border border-dashed border-slate-300 px-4 py-2 text-xs font-semibold text-slate-500 hover:border-shipany-blue hover:text-shipany-blue">+ Add condition</button>
            </div>
            <p className="mt-4 text-xs font-bold uppercase tracking-widest text-slate-400">THEN</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <select data-testid="rule-action-type" className={inputCls} value={editing.action.type} onChange={(e) => setEditing({ ...editing, action: { type: e.target.value, value: "" } })}>
                {ACTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <select data-testid="rule-action-value" className={`${inputCls} flex-1`} value={editing.action.value} onChange={(e) => setEditing({ ...editing, action: { ...editing.action, value: e.target.value } })}>
                <option value="">Select target…</option>
                {targetOptions.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <input type="number" data-testid="rule-priority" className={`${inputCls} mono w-20`} placeholder="P" value={editing.priority} onChange={(e) => setEditing({ ...editing, priority: e.target.value })} />
            </div>
            <label className="mt-3 flex items-center gap-2 text-sm font-medium text-slate-600">
              <input type="checkbox" data-testid="rule-enabled" checked={editing.enabled} onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })} className="h-4 w-4 rounded border-slate-300 text-shipany-blue" />
              Rule enabled
            </label>
            <button onClick={save} data-testid="rule-save-btn" className="mt-5 w-full rounded-xl bg-shipany-blue py-3 text-sm font-bold text-white hover:bg-[#0648B3]">Save Rule</button>
          </div>
        </div>
      )}
    </div>
  );
}
